import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between items-center text-center p-12 bg-[#0F1116] text-[#FFFDF5] relative overflow-hidden">
        <div
          className="absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-90 pointer-events-none"
          style={{ background: "#FF5722" }}
        />
        <div
          className="absolute -left-24 -bottom-24 h-64 w-64 rounded-full opacity-90 pointer-events-none"
          style={{ background: "#B8FF3B" }}
        />
        <Logo type="wordmark" variant="white" height={38} className="relative z-10" />
        <div className="relative z-10 max-w-md">
          <h1 className="text-5xl font-extrabold leading-[1.05] mb-5">
            Next steps,<br />
            <span style={{ color: "#B8FF3B" }}>one day</span> at a time.
          </h1>
          <p className="font-reading text-lg opacity-80 mx-auto max-w-sm">
            A discipleship journey for students — walk through The Story, The
            Knowledge, and The Connection with a mentor beside you.
          </p>
        </div>
        <p className="relative z-10 text-sm opacity-60">Growing — Next Steps For Students</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm animate-rise" data-testid="login-form">
          <div className="lg:hidden mb-8">
            <Logo type="wordmark" variant="black" height={34} />
          </div>
          <h2 className="text-3xl font-extrabold mb-1">Sign in</h2>
          <p className="text-muted-foreground mb-7 text-sm">Welcome back to Growing.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="mt-1 border-2 border-ink h-12 rounded-xl"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-1 border-2 border-ink h-12 rounded-xl"
                placeholder="••••••••"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full mt-6 h-12 rounded-full border-2 border-ink shadow-pop press text-base font-bold"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="flex items-center gap-3 my-5" aria-hidden="true">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground font-medium">OR</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            data-testid="login-google"
            className="w-full h-12 rounded-full border-2 border-ink bg-white hover:bg-muted press text-base font-bold gap-3"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-sm text-center mt-6 text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="font-bold text-foreground underline" data-testid="goto-register">
              Create an account
            </Link>
          </p>
          <p className="text-sm text-center mt-2 text-muted-foreground">
            Lead an organization?{" "}
            <Link to="/apply-leader" className="font-bold text-foreground underline" data-testid="goto-apply-leader">
              Apply to be listed
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
